/**
 * Cloud areas → OS geofence regions, as a pure function (card `t_23f46db0`,
 * plan `C:/Projects/Otto/docs/plans/GEOFENCING-E2E.md` §2.4; the iOS half is
 * card `t_e156b106`, plan `C:/Projects/Otto/docs/plans/GEOFENCING-IOS.md` §2.4).
 *
 * The cloud owns the areas (`GET /api/presence/areas?hubId=…`); the OS owns the
 * regions. This module is the whole translation, with no plugin, no store and
 * no fetch in it, so every rule below is pinned by a Node test:
 *
 *   - an area is registered under a **namespaced region id** (`area:<cloudId>`)
 *     so a region id read back off the device names the area it came from;
 *   - one area is one region (dedupe by area id — a later entry replaces an
 *     earlier one, which is what an edit looks like on the wire);
 *   - a radius below the OS floor is **registered at the floor and says so** in
 *     the region's stored note (plan §2.4: "If a cloud area's radius is below
 *     the OS minimum, register the minimum and say so in the region's stored
 *     note"), and the clamp is also reported to the caller;
 *   - more areas than the OS will hold are trimmed to the cap, keeping the
 *     cloud's own order (the last ones are reported as dropped, never silently).
 *
 * The OS limits live here as constants, per platform, with their measurement
 * recorded on them — they are the numbers both the app and the device evidence
 * quote, and `osLimitsFor()` is the one place a caller picks the pair:
 *
 *   - Android, `MAX_OS_REGIONS = 100` — per app, per device user. Documented
 *     limit of the Play Services geofencing API ("On single-user devices, there
 *     is a limit of 100 geofences per app. For multi-user devices, the limit is
 *     100 geofences per app per device user", developer.android.com → "Create
 *     and monitor geofences"); crossing it fails `addGeofences` with
 *     `GEOFENCE_TOO_MANY_GEOFENCES` (status code 1001).
 *   - Android, `MIN_OS_RADIUS_M = 100` — the same page's guidance for the
 *     smallest radius that still yields reliable transitions ("set a minimum
 *     radius of 100 meters … for best results … between 100-150 meters"):
 *     smaller regions do *register* without error and then miss transitions,
 *     which is worse than a clamped one, so the floor is applied here rather
 *     than trusted to the OS.
 *   - iOS, `IOS_MAX_OS_REGIONS` / `IOS_MIN_OS_RADIUS_M` — Apple's documented
 *     per-app cap, and the documented *reliability* distance the app floors a
 *     radius at (Apple documents **no** hard minimum radius; both sets of quotes
 *     are on the constants below, where the choice is auditable).
 *
 * The cloud's own bounds are 50..5000 m (`GEOFENCE_RADIUS_MIN_M` /
 * `GEOFENCE_RADIUS_MAX_M` in the cloud's `lib/geofenceAreas.ts`), so an area of
 * 50 m is a real case and the clamp is real work, not a formality.
 */

/** Google Play Services' per-app, per-user cap on registered geofences. */
export const MAX_OS_REGIONS = 100;

/** The smallest radius the OS is asked to monitor — the reliability floor. */
export const MIN_OS_RADIUS_M = 100;

/**
 * Apple's per-app cap on simultaneously monitored regions — the iOS twin of
 * `MAX_OS_REGIONS` (card `t_e156b106`, plan `GEOFENCING-IOS.md` §2.4).
 *
 * Documented verbatim on `CLLocationManager.startMonitoring(for:)`: "An app can
 * register up to 20 regions at a time."
 * (developer.apple.com/documentation/corelocation/cllocationmanager/startmonitoring(for:)).
 * The archived Location Awareness guide states the same rule from the system's
 * side — "For this reason, Core Location limits to 20 the number of regions that
 * may be simultaneously monitored by a single app." — and documents what a
 * caller sees when the space runs out: "If you attempt to register a region and
 * space is unavailable, the location manager calls the
 * locationManager:monitoringDidFailForRegion:withError: method of its delegate
 * with the kCLErrorRegionMonitoringFailure error code."
 * (developer.apple.com/library/archive → Location Awareness Programming Guide →
 * "Region Monitoring and iBeacon"). A region the OS could not hold fires no
 * event and fails only late, through a delegate the JS never sees — so the app
 * trims to the cap and reports the excess (`max-regions`) rather than asking
 * the OS to hold more than it can, exactly as it does for Play Services' 100.
 * The native twin holds the same number (card I1).
 */
export const IOS_MAX_OS_REGIONS = 20;

/**
 * The smallest radius this app registers on iOS — the floor it applies before a
 * region reaches Core Location (card `t_e156b106`).
 *
 * **Apple documents no hard minimum radius for a `CLCircularRegion`** — any
 * radius above 0 is accepted, silently — so this is the documented *reliability*
 * distance, the same discipline as Android's 100 m floor: the OS confirms a
 * crossing only past a movement threshold wider than a sub-200 m region, so
 * such a region cannot give reliable transitions. Apple's guide states the rule
 * and names the distance:
 *
 *  - "To prevent spurious notifications, iOS doesn't deliver region
 *    notifications until certain threshold conditions are met. Specifically, the
 *    user's location must cross the region boundary, move away from the boundary
 *    by a minimum distance, and remain at that minimum distance for at least 20
 *    seconds before the notifications are reported. … However, for testing
 *    purposes, you can assume that the minimum distance is approximately 200
 *    meters."
 *    (developer.apple.com/library/archive → Location Awareness Programming
 *    Guide → "Region Monitoring and iBeacon");
 *  - the floor sits inside the band Apple calls the timely one: "In iOS 6,
 *    regions with a radius between 1 and 400 meters work better on iPhone 4S or
 *    later devices."
 *    (developer.apple.com/documentation/corelocation/cllocationmanager/startmonitoring(for:));
 *  - and Apple leaves the size to the app: "choose a center point and radius
 *    that are appropriate and give you enough time to alert the user"
 *    (developer.apple.com/documentation/corelocation/clcircularregion/init(center:radius:identifier:)).
 *
 * The native twin records the same pair before clamping a region again
 * (`ios/App/App/BackgroundGeofence/GeofenceLimits.swift`, card I1): the app
 * clamps and reports first, the plugin clamps and reports again, so the two
 * numbers are one decision told twice. A clamp is reported to the caller and
 * stored on the region (`clampNote`) either way — nothing is silently enlarged.
 */
export const IOS_MIN_OS_RADIUS_M = 200;

/** The native platforms whose OS geofencing this app drives. */
export type NativeGeofencePlatform = 'android' | 'ios';

/** One platform's OS limits: the region cap and the radius floor. */
export interface OsLimits {
  maxRegions: number;
  minRadius: number;
}

/**
 * The OS limits a native platform's registration works inside. Android is the
 * module's original pair (`MAX_OS_REGIONS` / `MIN_OS_RADIUS_M`); iOS is Apple's
 * documented cap plus the floor from the constant above. Pure lookup — every
 * caller that builds regions takes its numbers from here.
 */
export function osLimitsFor(platform: NativeGeofencePlatform): OsLimits {
  return platform === 'ios'
    ? { maxRegions: IOS_MAX_OS_REGIONS, minRadius: IOS_MIN_OS_RADIUS_M }
    : { maxRegions: MAX_OS_REGIONS, minRadius: MIN_OS_RADIUS_M };
}

/** Region ids are namespaced so a device-side id names its area. */
export const REGION_ID_PREFIX = 'area:';

/** One area as the cloud serves it (`AREA_SELECT`, cloud `services/geofenceAreas.ts`). */
export interface CloudArea {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  radiusM: number;
}

/** One region as the native plugin takes it — and as it reads it back. */
export interface OsGeofenceRegion {
  id: string;
  latitude: number;
  longitude: number;
  radius: number;
  /** Stored with the region; says what was changed, when anything was. */
  note?: string;
}

/** A radius the OS floor moved, reported rather than hidden. */
export interface RadiusClamp {
  id: string;
  areaId: string;
  from: number;
  to: number;
}

export type AreaDropReason =
  | 'invalid-id'
  | 'invalid-coordinates'
  | 'invalid-radius'
  | 'max-regions';

export interface DroppedArea {
  /** The area's id when it had one, otherwise an empty string. */
  id: string;
  reason: AreaDropReason;
}

export interface RegionBuild {
  /** What to hand the OS, in the cloud's order. */
  regions: OsGeofenceRegion[];
  /** Every radius the floor moved, with both numbers. */
  clamped: RadiusClamp[];
  /** Every area that did not become a region, with the reason. */
  dropped: DroppedArea[];
  /** How many candidate areas the payload listed (after the shape read). */
  requested: number;
}

/** The plugin's own name for a region id, and back again. */
export function regionIdForArea(areaId: string): string {
  return `${REGION_ID_PREFIX}${areaId}`;
}

export function areaIdFromRegionId(regionId: string): string | null {
  return typeof regionId === 'string' && regionId.startsWith(REGION_ID_PREFIX)
    ? regionId.slice(REGION_ID_PREFIX.length) || null
    : null;
}

/** A number that is a real coordinate/radius, or `null`. */
function finite(value: unknown): number | null {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

/** A non-empty string, trimmed, or `null`. */
function nonEmpty(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

/** The area list a cloud payload carries, whatever shape it arrived in. */
function areaList(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  const areas = (payload as { areas?: unknown } | null | undefined)?.areas;
  return Array.isArray(areas) ? areas : [];
}

/**
 * The region note for a clamped area — what the device will answer with when
 * asked what it is monitoring, and what the morning's evidence quotes. English
 * on purpose: it is device/diagnostic copy, not a rendered string (nothing in
 * this module is user-facing).
 *
 * The "OS minimum" it names is the platform's pair from `osLimitsFor()` — on
 * iOS that floor is the documented reliability distance, because Apple
 * documents no hard minimum radius (see `IOS_MIN_OS_RADIUS_M`).
 */
export function clampNote(from: number, to: number): string {
  return `radius ${from} m clamped to the ${to} m OS minimum for reliable transitions`;
}

/**
 * Turn a cloud area payload into the regions the OS should monitor, applying
 * dedupe, the radius floor and the region cap, and reporting every change it
 * made. Total by contract: any shape (a partial payload, a null, an array of
 * junk) produces a `RegionBuild`, never a throw.
 */
export function buildRegions(
  payload: unknown,
  options: { maxRegions?: number; minRadius?: number } = {},
): RegionBuild {
  const maxRegions = options.maxRegions ?? MAX_OS_REGIONS;
  const minRadius = options.minRadius ?? MIN_OS_RADIUS_M;

  const clamped: RadiusClamp[] = [];
  const dropped: DroppedArea[] = [];
  const byId = new Map<string, OsGeofenceRegion>();
  /**
   * The clamp each region id currently needs, or `null` when it needs none —
   * keyed rather than appended so a re-listed area takes its earlier entry's
   * clamp with it (the report describes the regions that survive, not the
   * payload's history).
   */
  const clampById = new Map<string, RadiusClamp | null>();
  const list = areaList(payload);

  for (const raw of list) {
    if (!raw || typeof raw !== 'object') {
      dropped.push({ id: '', reason: 'invalid-id' });
      continue;
    }
    const area = raw as Record<string, unknown>;
    const id = nonEmpty(area.id);
    if (!id) {
      dropped.push({ id: '', reason: 'invalid-id' });
      continue;
    }

    const latitude = finite(area.latitude);
    const longitude = finite(area.longitude);
    if (
      latitude === null ||
      longitude === null ||
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      dropped.push({ id, reason: 'invalid-coordinates' });
      continue;
    }

    const radiusM = finite(area.radiusM);
    if (radiusM === null || radiusM <= 0) {
      dropped.push({ id, reason: 'invalid-radius' });
      continue;
    }

    const regionId = regionIdForArea(id);
    const radius = Math.max(radiusM, minRadius);
    const isClamped = radius !== radiusM;
    clampById.set(regionId, isClamped ? { id: regionId, areaId: id, from: radiusM, to: radius } : null);

    // Same area twice in one payload: the later entry is the newer state (an
    // edit re-listed by the cloud), so it replaces the earlier one outright —
    // including a clamp it no longer needs.
    byId.set(regionId, {
      id: regionId,
      latitude,
      longitude,
      radius,
      ...(isClamped ? { note: clampNote(radiusM, radius) } : {}),
    });
  }

  const regions: OsGeofenceRegion[] = [];
  for (const [id, region] of byId) {
    if (regions.length >= maxRegions) {
      dropped.push({ id, reason: 'max-regions' });
      continue;
    }
    regions.push(region);
    const clamp = clampById.get(id);
    if (clamp) clamped.push(clamp);
  }

  return { regions, clamped, dropped, requested: list.length };
}

/**
 * Whether two region sets are the same set — the rule that keeps the app from
 * re-registering geofences on every foreground (the OS asks that they be
 * re-registered only when required, developer.android.com → "Re-register
 * geofences only when required"). Coordinates are floats off a JSON payload and
 * a device store, so they are compared with a tolerance far below any real
 * movement (1e-6° ≈ 0.1 m); radii likewise (0.5 m).
 */
export function regionsEqual(a: readonly OsGeofenceRegion[], b: readonly OsGeofenceRegion[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x.id.localeCompare(y.id));
  const sortedB = [...b].sort((x, y) => x.id.localeCompare(y.id));
  for (let i = 0; i < sortedA.length; i += 1) {
    const left = sortedA[i];
    const right = sortedB[i];
    if (left.id !== right.id) return false;
    if (Math.abs(left.latitude - right.latitude) > 1e-6) return false;
    if (Math.abs(left.longitude - right.longitude) > 1e-6) return false;
    if (Math.abs(left.radius - right.radius) > 0.5) return false;
    if ((left.note ?? '') !== (right.note ?? '')) return false;
  }
  return true;
}

/** One line for the log: what a sync asked for and what it changed. */
export function regionBuildLine(build: RegionBuild): string {
  return (
    `[BackgroundGeofence] regions=${build.regions.length} requested=${build.requested} ` +
    `clamped=${build.clamped.length} dropped=${build.dropped.length}`
  );
}
